import { useState, useEffect } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { simularCampaña, generarConfiguracionAleatoria, generarAssumptions } from './simuladorLogic';
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import './App.css';

function Simulator() {
  // Estados principales
  const [presupuesto, setPresupuesto] = useState(200);
  const [calidad, setCalidad] = useState('media');
  const [targeting, setTargeting] = useState('amplio');
  const [plataforma, setPlataforma] = useState('instagram');
  const [producto, setProducto] = useState('fisico');
  const [estacionalidad, setEstacionalidad] = useState(0);
  
  // Estado para el modo comparativa
  const [modoComparativa, setModoComparativa] = useState(false);
  const [configComparativa, setConfigComparativa] = useState(null);
  
  // Cargar configuración guardada al iniciar
  useEffect(() => {
    const saved = localStorage.getItem('marketingSimulatorConfig');
    if (saved) {
      const config = JSON.parse(saved);
      setPresupuesto(config.presupuesto);
      setCalidad(config.calidad);
      setTargeting(config.targeting);
      setPlataforma(config.plataforma);
      setProducto(config.producto);
      setEstacionalidad(config.estacionalidad || 0);
    }
  }, []);
  
  // Guardar configuración cuando cambie
  useEffect(() => {
    const config = { presupuesto, calidad, targeting, plataforma, producto, estacionalidad };
    localStorage.setItem('marketingSimulatorConfig', JSON.stringify(config));
  }, [presupuesto, calidad, targeting, plataforma, producto, estacionalidad]);
  
  const resultados = simularCampaña(presupuesto, calidad, targeting, plataforma, producto, estacionalidad);
  const assumptions = generarAssumptions(calidad, targeting, presupuesto, estacionalidad, plataforma, producto, resultados.roas, resultados);
  
  // Datos para el gráfico (evolución del ROAS con presupuesto)
  const datosGrafico = [];
  for (let p = 100; p <= 2000; p += 200) {
    const sim = simularCampaña(p, calidad, targeting, plataforma, producto, estacionalidad);
    datosGrafico.push({ presupuesto: p, roas: sim.roas });
  }
  
  const generarAleatorio = () => {
    const random = generarConfiguracionAleatoria();
    setPresupuesto(random.presupuesto);
    setCalidad(random.calidad);
    setTargeting(random.targeting);
    setPlataforma(random.plataforma);
    setProducto(random.producto);
    setEstacionalidad(random.estacionalidad);
  };
  
  const copiarEnlace = () => {
    const config = { presupuesto, calidad, targeting, plataforma, producto, estacionalidad };
    const url = new URL(window.location.href);
    url.searchParams.set('config', btoa(JSON.stringify(config)));
    navigator.clipboard.writeText(url.toString());
    alert('✅ Enlace copiado con tu configuración');
  };
  
  const iniciarComparativa = () => {
    setConfigComparativa({
      presupuesto, calidad, targeting, plataforma, producto, estacionalidad, resultados
    });
    setModoComparativa(true);
  };
  
  const comparativaResults = configComparativa && simularCampaña(
    configComparativa.presupuesto,
    configComparativa.calidad,
    configComparativa.targeting,
    configComparativa.plataforma,
    configComparativa.producto,
    configComparativa.estacionalidad
  );
  
  const plataformaNombres = { 'instagram': 'Instagram', 'google': 'Google Ads', 'tiktok': 'TikTok' };
  const productoNames = { 'fisico': 'Producto físico', 'electronica': 'Electrónica', 'ebook': 'Ebook / Infoproducto' };
  
  return (
    <div className="simulator-container">
      <div className="premium-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <h1>Marketing Studio</h1>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button onClick={generarAleatorio} className="action-btn">🎲 Aleatorio</button>
            <button onClick={copiarEnlace} className="action-btn">📋 Copiar enlace</button>
            <button onClick={iniciarComparativa} className="action-btn">⚖️ Comparar</button>
            <a href="/" className="action-btn" style={{ textDecoration: 'none' }}>🏠 Inicio</a>
            {modoComparativa && <button onClick={() => setModoComparativa(false)} className="action-btn close-btn">✖ Cerrar</button>}
          </div>
        </div>
        
        <div className="subtitle">
          Simula campañas reales sin gastar dinero. Aprende qué funciona antes de invertir.
        </div>
        
        <div className="campaign-badge">
          {plataformaNombres[plataforma]} · {productoNames[producto]} · 💰 {resultados.precioMedio}€ precio medio
        </div>
        
        <div className="controls-grid">
          <div className="control-card">
            <div className="control-label">💰 Presupuesto: {presupuesto}€</div>
            <input type="range" value={presupuesto} onChange={(e) => setPresupuesto(Number(e.target.value))} min={50} max={2000} step={10} />
          </div>
          <div className="control-card">
            <div className="control-label">🎨 Calidad del anuncio</div>
            <select value={calidad} onChange={(e) => setCalidad(e.target.value)}>
              <option value="baja">Baja (creativo básico)</option>
              <option value="media">Media (diseño profesional)</option>
              <option value="alta">Alta (vídeo + copy experto)</option>
            </select>
          </div>
          <div className="control-card">
            <div className="control-label">🎯 Targeting</div>
            <select value={targeting} onChange={(e) => setTargeting(e.target.value)}>
              <option value="muy_amplio">Muy amplio</option><option value="amplio">Amplio</option><option value="restringido">Restringido</option>
            </select>
          </div>
          <div className="control-card">
            <div className="control-label">📱 Plataforma</div>
            <select value={plataforma} onChange={(e) => setPlataforma(e.target.value)}>
              <option value="instagram">Instagram</option><option value="google">Google Ads</option><option value="tiktok">TikTok</option>
            </select>
          </div>
          <div className="control-card">
            <div className="control-label">🏷️ Producto</div>
            <select value={producto} onChange={(e) => setProducto(e.target.value)}>
              <option value="fisico">Producto físico</option><option value="electronica">Electrónica</option><option value="ebook">Ebook</option>
            </select>
          </div>
          <div className="control-card">
            <div className="control-label">📈 Estacionalidad: +{estacionalidad}%</div>
            <input type="range" value={estacionalidad} onChange={(e) => setEstacionalidad(Number(e.target.value))} min={0} max={80} step={5} />
          </div>
        </div>
        
        {!modoComparativa ? (
          <>
            <div className="results-grid">
              {/* Impresiones */}
              <div 
                className="result-card" 
                data-tooltip-id="tooltip" 
                data-tooltip-content="Número de personas que han visto tu anuncio. Depende del presupuesto y la plataforma."
              >
                <div className="result-label">Impresiones</div>
                <div className="result-value">{resultados.impresiones.toLocaleString()}</div>
              </div>

              {/* CTR */}
              <div 
                className="result-card" 
                data-tooltip-id="tooltip" 
                data-tooltip-content="Click-Through Rate: porcentaje de personas que hacen clic en tu anuncio después de verlo. Más calidad = más CTR."
              >
                <div className="result-label">CTR</div>
                <div className="result-value">{resultados.ctr}%</div>
              </div>

              {/* Clics */}
              <div 
                className="result-card" 
                data-tooltip-id="tooltip" 
                data-tooltip-content="Número total de clics que recibe tu anuncio. Depende de las impresiones y del CTR."
              >
                <div className="result-label">Clics</div>
                <div className="result-value">{resultados.clics.toLocaleString()}</div>
              </div>

              {/* CPC */}
              <div 
                className="result-card" 
                data-tooltip-id="tooltip" 
                data-tooltip-content="Coste Por Clic: cuánto pagas en promedio por cada clic. Más bajo es mejor."
              >
                <div className="result-label">CPC</div>
                <div className="result-value">{resultados.cpc}€</div>
              </div>

              {/* Ventas */}
              <div 
                className="result-card" 
                data-tooltip-id="tooltip" 
                data-tooltip-content="Número estimado de ventas. Se calcula multiplicando los clics por la tasa de conversión (que varía según producto)."
              >
                <div className="result-label">Ventas</div>
                <div className="result-value">{resultados.ventas}</div>
              </div>

              {/* ROAS */}
              <div 
                className="result-card" 
                data-tooltip-id="tooltip" 
                data-tooltip-content="Return On Ad Spend: ingresos generados por cada euro invertido. >2x es rentable, >4x es excelente."
              >
                <div className="result-label">ROAS</div>
                <div className="result-value">{resultados.roas}x</div>
              </div>
            </div>
            
            <div className="chart-container">
              <h3>📈 Evolución del ROAS según presupuesto</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={datosGrafico}>
                  <XAxis dataKey="presupuesto" />
                  <YAxis />
                  <RechartsTooltip />
                  <Line type="monotone" dataKey="roas" stroke="#667eea" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <div className="comparativa-grid">
            <div className="comparativa-col">
              <h3>Configuración actual</h3>
              <p>💰 {presupuesto}€ | 🎨 {calidad} | 🎯 {targeting}<br />📱 {plataforma} | 🏷️ {producto} | 📈 +{estacionalidad}%</p>
              <div className="results-grid mini">
                <div className="result-card"><div className="result-label">ROAS</div><div className="result-value">{resultados.roas}x</div></div>
                <div className="result-card"><div className="result-label">Ventas</div><div className="result-value">{resultados.ventas}</div></div>
              </div>
            </div>
            <div className="comparativa-col">
              <h3>Configuración guardada</h3>
              <p>💰 {configComparativa?.presupuesto}€ | 🎨 {configComparativa?.calidad} | 🎯 {configComparativa?.targeting}<br />📱 {configComparativa?.plataforma} | 🏷️ {configComparativa?.producto} | 📈 +{configComparativa?.estacionalidad}%</p>
              <div className="results-grid mini">
                <div className="result-card"><div className="result-label">ROAS</div><div className="result-value">{comparativaResults?.roas}x</div></div>
                <div className="result-card"><div className="result-label">Ventas</div><div className="result-value">{comparativaResults?.ventas}</div></div>
              </div>
            </div>
          </div>
        )}
        
        <div className="analysis-card">
          <div className="analysis-title">🔍 Análisis inteligente</div>
          <ul className="analysis-list">
            {resultados.roas < 2 && <li>⚠️ ROAS bajo ({resultados.roas}x). Mejora calidad o targeting.</li>}
            {resultados.roas >= 2 && resultados.roas < 4 && <li>✅ Buen ROAS ({resultados.roas}x). Campaña rentable.</li>}
            {resultados.roas >= 4 && <li>🎉 ROAS excelente ({resultados.roas}x). ¡Escala ya!</li>}
            {plataforma === 'google' && <li>🔍 Google Ads: mayor intención de compra.</li>}
            {plataforma === 'tiktok' && <li>📱 TikTok: gran alcance viral.</li>}
            {estacionalidad > 30 && <li>🎄 Alta estacionalidad: aprovecha el pico de demanda.</li>}
          </ul>
        </div>

        {/* Panel de Assumptions - AHORA DENTRO DEL PREMIUM-CARD */}
        <div className="assumptions-panel">
          <h3>🔬 ¿Por qué este resultado?</h3>
          <p className="assumptions-resumen" style={{ color: assumptions.colorResumen, fontWeight: 'bold' }}>
            {assumptions.resumen}
          </p>
          <div className="assumptions-grid">
            {assumptions.explicaciones.map((exp, idx) => (
              <div key={idx} className={`assumption-card ${exp.impacto}`}>
                <div className="assumption-header">
                  <span className="assumption-icon">{exp.variable.split(' ')[0]}</span>
                  <strong>{exp.variable}</strong>
                  <span className={`impacto-badge ${exp.impacto}`}>
                    {exp.impacto === 'positivo' && '✅ Impacto +'}
                    {exp.impacto === 'negativo' && '⚠️ Impacto -'}
                    {exp.impacto === 'negativo parcial' && '⚠️ Impacto +/-'}
                    {exp.impacto === 'rendimientos decrecientes' && '📉 Rendimiento decreciente'}
                    {exp.impacto === 'limitado' && '💰 Presupuesto bajo'}
                    {exp.impacto === 'alcance viral' && '📱 Alcance viral'}
                    {exp.impacto === 'positivo alto' && '🔥 Impacto muy alto'}
                    {exp.impacto === 'conversión más lenta' && '🐢 Conversión lenta'}
                    {exp.impacto === 'conversión rápida' && '⚡ Conversión rápida'}
                    {exp.impacto === 'neutral' && '⚪ Neutral'}
                  </span>
                </div>
                <p className="assumption-desc">{exp.descripcion}</p>
                <p className="assumption-sugerencia">💡 {exp.sugerencia}</p>
              </div>
            ))}
          </div>
        </div>
        <ReactTooltip id="tooltip" place="top" effect="solid" />
      </div>
    </div>
  );
}

export default Simulator;